import React, { useEffect, useMemo, useState } from 'react';
import { ArrowBack, Search } from "@mui/icons-material";
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';

// Configuração do catálogo operacional de um projeto (SYNCORP-912, Task 3):
// escolhe o projeto, busca no catálogo global de espécies e
// habilita/desabilita em lote — sem precisar de SQL. Enquanto um projeto não
// tiver nenhuma espécie habilitada aqui, o catálogo global inteiro continua
// valendo pra ele (fallback controlado no backend), então essa tela é
// puramente opcional/de refinamento, nunca bloqueante.
const CatalogoProjetoEspecies = () => {
  const [projetos, setProjetos] = useState<any[]>([]);
  const [uidProjeto, setUidProjeto] = useState('');
  const [todasEspecies, setTodasEspecies] = useState<any[]>([]);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    api.get('/pesca/projetos').then((res) => setProjetos(res.data.data));
    api.get('/pesca/especies?incluir_inativas=false').then((res) => setTodasEspecies(res.data.data));
  }, []);

  useEffect(() => {
    if (!uidProjeto) {
      setSelecionadas(new Set());
      return;
    }
    setCarregando(true);
    setSalvo(false);
    api.get(`/pesca/projetos/${uidProjeto}/especies`)
      .then((res) => setSelecionadas(new Set(res.data.data.map((e: any) => e.uid_pesca_especie))))
      .finally(() => setCarregando(false));
  }, [uidProjeto]);

  const especiesFiltradas = useMemo(() => {
    const termo = search.trim().toLowerCase();
    if (!termo) return todasEspecies;
    return todasEspecies.filter((e) =>
      e.nome_popular?.toLowerCase().includes(termo) || e.nome_cientifico?.toLowerCase().includes(termo)
    );
  }, [todasEspecies, search]);

  const toggle = (uid: string) => {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (novo.has(uid)) novo.delete(uid); else novo.add(uid);
      return novo;
    });
  };

  const selecionarTodosVisiveis = () => {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      especiesFiltradas.forEach((e) => novo.add(e.uid_pesca_especie));
      return novo;
    });
  };

  const limparTodosVisiveis = () => {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      especiesFiltradas.forEach((e) => novo.delete(e.uid_pesca_especie));
      return novo;
    });
  };

  const salvar = async () => {
    if (!uidProjeto) return;
    setSalvando(true);
    setErro('');
    setSalvo(false);
    try {
      await api.put(`/pesca/projetos/${uidProjeto}/especies`, {
        uid_pesca_especies: Array.from(selecionadas),
      });
      setSalvo(true);
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar catálogo do projeto.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Catálogo por Projeto</h1>
        <div className={styles.actionsHeader}>
          <button className={styles.blueBtn} onClick={() => window.history.back()} title="Voltar">
            <ArrowBack />
          </button>
        </div>
      </div>

      <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-8px', marginBottom: '16px' }}>
        Escolha um projeto e marque as espécies que fazem parte do catálogo operacional dele. Enquanto nenhuma
        espécie estiver marcada, o projeto continua usando o catálogo global inteiro.
      </p>

      <div style={{ background: '#fff', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
        <label style={labelStyle}>Projeto</label>
        <select style={{ ...inputStyle, maxWidth: '360px' }} value={uidProjeto} onChange={(e) => setUidProjeto(e.target.value)}>
          <option value="">Selecione um projeto...</option>
          {projetos.map((p) => (
            <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>
          ))}
        </select>
      </div>

      {uidProjeto && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
              <Search style={{ position: 'absolute', left: '10px', top: '10px', color: '#888' }} />
              <input
                type="text"
                placeholder="Buscar no catálogo global..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, padding: '8px 10px 8px 36px' }}
              />
            </div>
            <button type="button" style={secondaryBtnStyle} onClick={selecionarTodosVisiveis}>Marcar todos (filtro)</button>
            <button type="button" style={secondaryBtnStyle} onClick={limparTodosVisiveis}>Desmarcar todos (filtro)</button>
            <span style={{ fontSize: '0.85rem', color: '#666' }}>
              <strong style={{ color: 'var(--green-700)' }}>{selecionadas.size}</strong> habilitada(s)
            </span>
          </div>

          {carregando ? (
            <p style={{ color: '#999' }}>Carregando catálogo do projeto...</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '420px', overflowY: 'auto', padding: '4px' }}>
              {especiesFiltradas.map((e) => (
                <label key={e.uid_pesca_especie} style={chipStyle(selecionadas.has(e.uid_pesca_especie))}>
                  <input
                    type="checkbox"
                    style={{ marginRight: '6px' }}
                    checked={selecionadas.has(e.uid_pesca_especie)}
                    onChange={() => toggle(e.uid_pesca_especie)}
                  />
                  {e.nome_popular}
                </label>
              ))}
              {especiesFiltradas.length === 0 && (
                <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhuma espécie encontrada.</span>
              )}
            </div>
          )}

          {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem', marginTop: '10px' }}>{erro}</p>}
          {salvo && <p style={{ color: 'var(--green-700)', fontSize: '0.85rem', marginTop: '10px' }}>Catálogo do projeto atualizado.</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button style={primaryBtnStyle} disabled={salvando} onClick={salvar}>
              {salvando ? 'Salvando...' : 'Salvar catálogo do projeto'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '6px 14px', borderRadius: '16px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap',
};
const chipStyle = (ativo: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: '16px',
  border: `1px solid ${ativo ? '#03c9fe' : '#ddd'}`, background: ativo ? '#e6f9ff' : '#fff',
  fontSize: '0.85rem', cursor: 'pointer',
});

export default CatalogoProjetoEspecies;
